import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Briefcase, MapPin, Clock } from "@phosphor-icons/react";
import { Avatar } from "@/components/Avatar";
import { api } from "@/lib/api";

const money = (j) => {
  if (!j.pay_min && !j.pay_max) return "Pay on application";
  const cur = (j.currency || "usd").toUpperCase();
  const range = j.pay_max && j.pay_max !== j.pay_min
    ? `${j.pay_min.toLocaleString()} – ${j.pay_max.toLocaleString()}`
    : `${(j.pay_max || j.pay_min).toLocaleString()}`;
  return `${cur} ${range} per ${j.pay_period}`;
};

export default function JobPublic() {
  const { token } = useParams();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/jobs/public/${token}`)
      .then(({ data }) => setJob(data))
      .catch(() => setError("That vacancy is no longer shared publicly."));
  }, [token]);

  if (error) return <div data-testid="public-job-error" className="p-10 text-center text-sm text-slate-500">{error}</div>;
  if (!job) return <div className="p-10 text-center text-sm text-slate-500">Loading…</div>;

  return (
    <div data-testid="public-job-page" className="mx-auto max-w-2xl p-5 sm:p-10">
      <p className="overline text-slate-500">Unity &amp; Utility · Vacancy {job.reference}</p>
      <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl" data-testid="public-job-title">
        {job.title}
      </h1>
      <div className="mt-3 flex flex-wrap gap-3 text-sm text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <Avatar imageId={job.employer_avatar_id} sig={job.employer_avatar_sig} name={job.employer_name} size="sm" testid="public-job-avatar" />
          {job.employer_name}
        </span>
        <span className="inline-flex items-center gap-1.5"><MapPin size={15} /> {[job.city, job.country].filter(Boolean).join(", ")}</span>
        <span className="inline-flex items-center gap-1.5"><Clock size={15} /> {job.employment_type}</span>
      </div>
      <p className="mt-4 border border-border bg-slate-50 px-3 py-2 text-sm font-semibold text-[#002FA7]" data-testid="public-job-pay">
        {money(job)}
      </p>
      {job.description && <p className="mt-5 whitespace-pre-line text-sm leading-relaxed text-slate-700">{job.description}</p>}
      {(job.requirements || []).length > 0 && (
        <div className="mt-5">
          <div className="overline text-slate-500">What you need</div>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">
            {job.requirements.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
      {job.scheme_name && (
        <p className="mt-5 border border-[#A7E3CE] bg-[#E6F7F1] px-3 py-2 text-xs text-[#0B7A5B]">
          Supported by the {job.scheme_name} scheme.
        </p>
      )}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <Link
          to="/register"
          data-testid="public-job-apply-link"
          className="bg-[#002FA7] px-5 py-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#00227A]"
        >
          Create an account to apply
        </Link>
        <Link to="/login" className="text-sm font-semibold text-[#002FA7] hover:underline">Already a member? Sign in</Link>
      </div>
      {job.closing_date && (
        <p className="mt-4 text-xs text-slate-500">Applications close {job.closing_date}.</p>
      )}
    </div>
  );
}
